import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useGetBranchesQuery } from "../../../admin/adminApi";
import { useGetProductBranchMapQuery, useSetProductBranchMapMutation } from "../productsApi";

// Branch mapping (handoff §13.5) - a different, additional capability from
// the "Linked in Other Branches" panel: this product stays ONE row at its
// home branch, this just marks which OTHER branches can also see/use it (no
// second row, nothing to re-enter). Deliberately plain checkboxes, not a
// multi-select dropdown - per instruction, the people using this screen
// aren't computer-savvy, so "tick the branches you want" beats any fancier
// picker. Explicit Save (not save-on-toggle) matches every other master
// screen in this app.
const BranchAvailabilityPanel = ({ branchId, productCode, homeBranchCode, readOnly }) => {
  const { data: branches = [] } = useGetBranchesQuery();
  const { data: mapped = [], isLoading } = useGetProductBranchMapQuery({ branchId, productCode });
  const [setBranchMap, { isLoading: isSaving }] = useSetProductBranchMapMutation();
  const [selected, setSelected] = useState(new Set());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSelected(new Set(mapped.filter((m) => m.isActive).map((m) => m.branchId)));
    setDirty(false);
  }, [mapped]);

  const otherBranches = branches.filter((b) => b.branchId !== Number(branchId));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await setBranchMap({ branchId, productCode, branchIds: [...selected] }).unwrap();
      toast.success("Branch availability updated");
      setDirty(false);
    } catch (error) {
      toast.error(error?.message || "Could not update branch availability");
    }
  };

  if (otherBranches.length === 0) return null;

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-2">Also Show This Product At</h5>
        <p className="text-muted small mb-3">
          Tick a branch to let that branch use this same product too - it still belongs to{" "}
          {homeBranchCode}, nothing is copied or re-entered.
        </p>
        {isLoading ? (
          <p className="text-muted mb-0">Loading...</p>
        ) : (
          <>
            <div className="row mb-3">
              {otherBranches.map((b) => (
                <div className="col-md-4 mb-2" key={b.branchId}>
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`prodbranchmap-${b.branchId}`}
                      style={{ width: "2.5em", height: "1.4em" }}
                      checked={selected.has(b.branchId)}
                      onChange={() => toggle(b.branchId)}
                      disabled={readOnly}
                    />
                    <label className="form-check-label ms-2" htmlFor={`prodbranchmap-${b.branchId}`}>
                      {b.branchCode}
                    </label>
                  </div>
                </div>
              ))}
            </div>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={!dirty || isSaving}
              >
                Save
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BranchAvailabilityPanel;
